<template>
  <!-- Card banner across the page width, in the Get in touch band's pale blue and glow.
       Content is left-aligned and runs the full width of the card.
       No partner logo until Anthropic approves its use (website feedback). -->
  <sec-section :divider="false" labelledby="about-partnership" class="about-partner">
    <div class="about-partner__card">
      <sec-section-heading
        id="about-partnership"
        align="start"
        :eyebrow="partnership.eyebrow"
        :title="partnership.title"
      />
      <p
        v-for="(p, i) in partnership.paragraphs"
        :key="i"
        class="m500 sec-body-lg about-partner__text"
        :class="{ 'about-partner__highlight': p.lead }"
        data-reveal
      >
        <sec-rich :value="p" />
      </p>
      <a
        class="m600 about-partner__link"
        :href="partnership.link.href"
        target="_blank"
        rel="noopener"
        data-reveal
      >
        {{ partnership.link.label }}
        <v-icon size="18" aria-hidden="true">mdi-arrow-top-right</v-icon>
      </a>
    </div>
  </sec-section>
</template>

<script setup>
import SecSection from '@/components/security/ui/SecSection.vue';
import SecSectionHeading from '@/components/security/ui/SecSectionHeading.vue';
import SecRich from '@/components/security/ui/SecRich.vue';
import { ABOUT } from '@/content/security/about';

const partnership = ABOUT.partnership;
</script>

<style scoped>
/* Same surface as SecCtaBand ("Get in touch with us!"): pale DIGU Blue with a soft glow. */
.about-partner__card {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: var(--sec-s3);
  padding: 2.5rem 1.25rem;
  text-align: left;
  background: var(--sec-accent-subtle);
  border-radius: 20px;
}

.about-partner__card::before {
  content: '';
  position: absolute;
  inset: -40% 0;
  background: radial-gradient(ellipse 45% 50% at 50% 50%, var(--sec-accent-ring), transparent 100%);
  opacity: 0.45;
  pointer-events: none;
}

.about-partner__card > * {
  position: relative;
}

.about-partner__text {
  max-width: none;
}

.about-partner__text :deep(b) {
  color: var(--sec-ink);
}

/* DIGU's own status: a white card with a DIGU Blue rule on its left edge. */
.about-partner__highlight {
  padding: 1.5rem 1.75rem;
  color: var(--sec-ink-2);
  background: var(--sec-surface);
  border-left: 4px solid var(--sec-accent);
  border-radius: 0 var(--sec-radius-card) var(--sec-radius-card) 0;
  box-shadow: 0 18px 36px -24px var(--sec-header-shadow);
}

/* Block sized to its text; the arrow flows inline after the last word. */
.about-partner__link {
  display: block;
  width: fit-content;
  max-width: 100%;
  font-size: 1.0625rem;
  color: var(--sec-accent-text);
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease;
}

.about-partner__link:hover,
.about-partner__link:focus-visible {
  border-bottom-color: var(--sec-accent-text);
}

.about-partner__link .v-icon {
  margin-left: 4px;
  vertical-align: -3px;
  transition: transform 0.2s ease;
}

.about-partner__link:hover .v-icon {
  transform: translate(2px, -2px);
}

@media only screen and (min-width: 700px) {
  .about-partner__card {
    padding: 3.5rem;
  }
  .about-partner__highlight {
    padding: 1.75rem 2.25rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .about-partner__link .v-icon {
    transition: none;
  }
  .about-partner__link:hover .v-icon {
    transform: none;
  }
}
</style>
